import { ToastManagerRepository } from "@core/domain/repositories/toast-manager.repository";
import type { Nullable } from "@core/domain/types/nullable.type";
import { LoadLayoutStore } from "@core/presentation/components/feedback/load-layout/load-layout.store";
import { BaseViewModel } from "@iot-platform/core";
import type {
    NotebookMessage,
    NotebookMessageUpdate,
} from "@notebook/domain/models/notebook-message.model";
import { NotebookMessageUpdateUseCase } from "@notebook/domain/usecases/notebook-message-update.usecase";
import type { MessageModalFormValuesValidated } from "@notebook/presentation/components/diary/message-modal-form.view";
import { inject, injectable } from "inversify";
import { action, makeObservable, observable, runInAction } from "mobx";

@injectable()
export class EditMessageModalViewModel extends BaseViewModel {
    @observable
    initialLoading = true;

    @observable
    notebookId: Nullable<string> = "";

    @observable
    notebookMessage: Nullable<Readonly<NotebookMessage>> = null;

    constructor(
        @inject(NotebookMessageUpdateUseCase)
        private readonly notebookMessageUpdateUseCase: NotebookMessageUpdateUseCase,
    ) {
        super();
        makeObservable(this);
    }

    override didMount(): void {
        this.initViewData();
    }

    @action
    initViewData(): void {
        runInAction(() => {
            this.initialLoading = false;
        });
    }

    @action
    async editMessage(
        values: MessageModalFormValuesValidated,
    ): Promise<Nullable<NotebookMessage>> {
        if (!this.notebookId || !this.notebookMessage)
            throw new Error("Missing notebookId or notebookMessage");

        LoadLayoutStore.start();

        const notebookMessageData: NotebookMessageUpdate = {
            id: Number(this.notebookMessage.id.value),
            title: values.title,
            description: values.description,
            time: values.time ?? undefined,
            userId: values.userId,
        };

        const messageEdited = await this.notebookMessageUpdateUseCase.execute(
            this.notebookMessage.id.value,
            this.notebookId,
            notebookMessageData,
        );

        if (messageEdited) {
            ToastManagerRepository.success();
        }

        LoadLayoutStore.finish();

        return messageEdited;
    }

    @action
    setNotebookMessage(notebookMessage: Readonly<NotebookMessage>): void {
        runInAction(() => {
            this.notebookMessage = notebookMessage;
        });
    }

    @action
    setNotebookId(notebookId: string): void {
        runInAction(() => {
            this.notebookId = notebookId;
        });
    }
}
